import React, { useState, useEffect, useRef } from "react";
import {
  Menu,
  MenuItem,
  Box,
  Typography,
  useTheme,
  useMediaQuery,
  FormControl,
  Select,
  SelectChangeEvent,
} from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { useAppStore } from "../../../stores/appStore";
import { ReactionBlock } from "./reaction-block/reaction-block";
import {
  menuComponentStyles,
  copyLinkMenuItemStyles,
  menuItemStyles,
} from "./styles";
import { Message, MessageMenuItem } from "../../../types";
import { MessageMenuVisualisation } from "./message-menu-visualisation";
import { styled } from "@mui/material/styles";
import { motion } from "framer-motion";
import useChatStore from "../../../stores/chatStore";
import { MessageFeedbackModal } from "../message-feedback-modal/message-feedback-modal";

const TagInput = styled("input")(({ theme }) => ({
  flexGrow: 1,
  marginRight: theme.spacing(1),
  padding: theme.spacing(1.5),
  borderRadius: theme.shape.borderRadius,
  border: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.paper,
  color: theme.palette.text.primary,
  fontSize: "15px",
  transition: "all 0.2s ease-in-out",
  boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
  "::placeholder": {
    color: theme.palette.text.secondary,
    opacity: 0.8,
  },
  "&:focus": {
    outline: "none",
    borderColor: theme.palette.primary.main,
    boxShadow: `0 0 0 3px ${theme.palette.primary.light}33`,
  },
  [theme.breakpoints.down("md")]: {
    marginRight: theme.spacing(0.75),
    padding: theme.spacing(1),
    fontSize: "14px",
  },
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(0.75),
    fontSize: "13px",
  },
}));

const SendButton = styled(motion.button)(({ theme }) => ({
  padding: theme.spacing(1.2, 2),
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
  border: "none",
  borderRadius: theme.shape.borderRadius,
  cursor: "pointer",
  fontSize: "15px",
  transition: "transform 0.1s ease",
  boxShadow: "0 2px 4px rgba(0, 0, 0, 0.15)",
  outline: "none",
  "&:hover": {
    backgroundColor: theme.palette.primary.dark,
  },
  [theme.breakpoints.down("md")]: {
    padding: theme.spacing(0.75, 1.5),
    fontSize: "14px",
  },
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(0.5, 1.25),
    fontSize: "13px",
  },
}));

// const containerVariants = {
//   hidden: { opacity: 0, y: 20 },
//   visible: { opacity: 1, y: 0, transition: { duration: 0.4 } },
// };

export type MessageMenuDropdownProps = {
  message: Message;
  menuPosition: { top: number; left: number } | null;
  handleClose: () => void;
  handleReactionClick: (reactionKey: string) => void;
  handleCopyLink: () => void;
  handleCopyMessageText: () => void;
  menuItems?: MessageMenuItem[];
  menuItemsLoading?: boolean;
  handleSendMenuItem: (
    text: string,
    description: string,
    chosen_text?: string
  ) => void;
  handleAddToFavorite: () => void;
  handleAddToTag: (tag: string) => void;
  handleRenameTag: (newTags: string[]) => void;
  isSaved?: boolean;
  text?: string;
  selectedText?: string;
  hasUserSelectedText?: boolean;
  tags?: string[];
  previousUserMessage?: Message;
  onFeedbackSubmitted?: () => void;
};

export const MessageMenuDropdown: React.FC<MessageMenuDropdownProps> = ({
  message,
  menuPosition,
  handleClose,
  handleReactionClick,
  handleCopyLink,
  menuItems = [],
  menuItemsLoading = false,
  handleSendMenuItem,
  handleAddToFavorite,
  handleCopyMessageText,
  text,
  selectedText,
  hasUserSelectedText = false,
  handleAddToTag,
  handleRenameTag,
  tags,
  previousUserMessage,
  onFeedbackSubmitted,
}) => {
  const { markedIds } = useAppStore();
  const { tagMessages, fetchTagMessages } = useChatStore();
  const theme = useTheme();
  const isCompactLayout = useMediaQuery(theme.breakpoints.down("lg"));
  const existingTags: string[] = tags || message.tag || [];
  const [tagInputVisible, setTagInputVisible] = useState(false);
  const [tagText, setTagText] = useState("");
  const [tagMode, setTagMode] = useState<"add" | "edit" | null>(null);
  const [editingTagIndex, setEditingTagIndex] = useState<number | null>(null);
  const [copyMessage, setCopyMessage] = useState("");
  const [tagSuggestionsOpen, setTagSuggestionsOpen] = useState(false);
  const [adjustedMenuPosition, setAdjustedMenuPosition] = useState<{
    top: number;
    left: number;
  } | null>(null);
  const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false);
  const isMobile = useRef(false);

  const menuPositionRef = useRef(menuPosition);

  useEffect(() => {
    menuPositionRef.current = menuPosition;
  }, [menuPosition]);

  useEffect(() => {
    if (!tagInputVisible || tagMode !== "add") {
      setTagSuggestionsOpen(false);
    }
  }, [tagInputVisible, tagMode]);

  useEffect(() => {
    const checkIsMobile = () => {
      const isMobileByUserAgent =
        /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
          navigator.userAgent
        );
      const isMobileByScreenSize = window.innerWidth <= 768;
      isMobile.current = isMobileByUserAgent || isMobileByScreenSize;
    };
    checkIsMobile();
    window.addEventListener("resize", checkIsMobile);
    return () => {
      window.removeEventListener("resize", checkIsMobile);
    };
  }, []);

  useEffect(() => {
    if (menuPosition) {
      let newPosition = { top: menuPosition.top, left: menuPosition.left };

      if (isMobile.current && tagInputVisible) {
        const viewportHeight = window.innerHeight;
        newPosition = {
          top: Math.min(menuPosition.top, viewportHeight - 300),
          left: menuPosition.left,
        };
      }

      setAdjustedMenuPosition(newPosition);
    } else {
      setAdjustedMenuPosition(null);
    }
  }, [menuPosition, tagInputVisible]);

  useEffect(() => {
    if (!menuPosition) {
      setTagInputVisible(false);
      setTagText("");
      setTagMode(null);
      setEditingTagIndex(null);
      setCopyMessage("");
      setTagSuggestionsOpen(false);
    }
  }, [menuPosition]);

  useEffect(() => {
    if (menuPosition) {
      fetchTagMessages();
    }
  }, [fetchTagMessages, menuPosition]);

  const handleInputFocus = () => {
    if (isMobile.current) {
      setTimeout(() => {
        const currentMenuPosition = menuPositionRef.current;
        if (currentMenuPosition) {
          const viewportHeight = window.innerHeight;
          const safeTop = Math.min(
            currentMenuPosition.top,
            viewportHeight - 350
          );

          setAdjustedMenuPosition({
            top: safeTop,
            left: currentMenuPosition.left,
          });

          window.scrollTo({
            top: safeTop - 50,
            behavior: "smooth",
          });
        }
      }, 300);
    }
  };

  const suggestions = tagText
    ? tagMessages.filter(
        (t: string) =>
          t.toLowerCase().includes(tagText.toLowerCase()) &&
          !existingTags.some(
            (existingTag) => existingTag.toLowerCase() === t.toLowerCase()
          )
      )
    : tagMessages.filter(
        (t: string) =>
          !existingTags.some(
            (existingTag) => existingTag.toLowerCase() === t.toLowerCase()
          )
      );

  const onTagSubmit = () => {
    const trimmedTag = tagText.trim();
    if (!trimmedTag) return;

    if (
      existingTags.some((tag) => tag.toLowerCase() === trimmedTag.toLowerCase())
    ) {
      setCopyMessage("This tag already exists!");
      return;
    }
    if (tagMode === "add") {
      if (existingTags.length >= 3) {
        setCopyMessage("You cannot add more than 3 tags");
        return;
      }
      handleAddToTag(trimmedTag);
    } else if (tagMode === "edit" && editingTagIndex !== null) {
      const newTags = [...existingTags];
      newTags[editingTagIndex] = trimmedTag;
      handleRenameTag(newTags);
    }

    setTagText("");
    setTagInputVisible(false);
    setTagMode(null);
    setEditingTagIndex(null);
    handleClose();
  };

  const hasValidPosition =
    adjustedMenuPosition !== null && menuPosition !== null;

  const handleReportMessage = () => {
    setIsFeedbackModalOpen(true);
    handleClose();
  };

  // const isMultipleChoiceQuiz =
  //   message.buttons &&
  //   message.buttons.some((buttonGroup) =>
  //     buttonGroup.some((button) => button.isCorrect !== undefined)
  //   );

  // Bot messages with reaction icons only (not button/suggestion bubbles,
  // which have no likes). Includes images/visualizations with empty menu payload.
  const shouldShowReportButton =
    message.is_bot && message.type !== "button" && !menuItemsLoading;

  const handleCloseFeedbackModal = () => {
    setIsFeedbackModalOpen(false);
  };

  const feedbackText =
    message.type === "tabbed_message"
      ? text
      : selectedText && selectedText.trim()
        ? selectedText
        : text;

  return (
    <>
      <Menu
        autoFocus={true}
        disableAutoFocusItem={false}
        open={Boolean(menuPosition) && hasValidPosition && !menuItemsLoading}
        onClose={handleClose}
        anchorReference="anchorPosition"
        disableScrollLock={false}
        anchorPosition={
          hasValidPosition
            ? {
                top: adjustedMenuPosition.top - 15,
                left: adjustedMenuPosition.left + 15,
              }
            : undefined
        }
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        PaperProps={{
          sx: {
            ...(tagInputVisible && {
              maxHeight: "none",
              overflow: "visible",
            }),
            [theme.breakpoints.down("md")]: {
              maxHeight: "none",
              overflowY: "visible",
              overflowX: "visible",
            },
          },
        }}
        sx={{
          zIndex: theme.zIndex.tooltip,
          ...menuComponentStyles,
          "& .MuiMenuItem-root": {
            backgroundColor: theme.palette.messageMenuDropdown.bg,
            borderTop: `1px solid ${theme.palette.messageMenuDropdown.border}`,
          },
          "& .MuiMenuItem-root:first-of-type": {
            backgroundColor: theme.palette.messageMenuDropdown.bg,
            borderTop: "none",
          },
          "& .MuiMenuItem-root:only-of-type": {
            backgroundColor: theme.palette.messageMenuDropdown.bg,
            borderTop: "none",
          },
          "& .MuiMenuItem-root:last-of-type": {
            backgroundColor: theme.palette.messageMenuDropdown.bg,
            borderBottomRightRadius: "18px",
            borderBottomLeftRadius: "18px",
          },
          ...(!text && {
            "& .MuiMenuItem-root:first-of-type": {
              backgroundColor: theme.palette.messageMenuDropdown.bg,
              borderTopLeftRadius: "18px",
              borderTopRightRadius: "18px",
            },
          }),
        }}
        classes={{ paper: "custom-menu-paper" }}
      >
        {!hasUserSelectedText && message.type !== "button" && (
          <Box>
            <ReactionBlock
              markedIds={markedIds}
              handleReactionClick={handleReactionClick}
              handleAddToFavorite={handleAddToFavorite}
              handleReportMessage={
                shouldShowReportButton ? handleReportMessage : undefined
              }
            />
          </Box>
        )}

        {text && (
          <MenuItem
            sx={copyLinkMenuItemStyles}
            onClick={handleCopyMessageText}
            disableRipple
            onMouseDown={(e) => e.stopPropagation()}
          >
            {selectedText ? "📋 Copy Selected Text" : "📋 Copy Message Text"}
          </MenuItem>
        )}

        {!hasUserSelectedText && (
          <MenuItem
            sx={menuItemStyles}
            onClick={handleCopyLink}
            disableRipple
            onMouseDown={(e) => e.stopPropagation()}
          >
            🔗 Copy Message Link
          </MenuItem>
        )}

        {!menuItemsLoading && (
          <MessageMenuVisualisation
            items={menuItems}
            selectedText={selectedText}
            text={text}
            handleSendMenuItem={handleSendMenuItem}
          />
        )}

        {existingTags.length < 3 && !tagInputVisible && (
          <MenuItem
            sx={menuItemStyles}
            onClick={() => {
              setTagMode("add");
              setTagInputVisible(true);
              setTagText("");
            }}
            disableRipple
            onMouseDown={(e) => e.stopPropagation()}
          >
            🔖 Add New Tag
          </MenuItem>
        )}

        {tagInputVisible && (
          <MenuItem
            disableRipple
            autoFocus
            dense
            onMouseDown={(e) => e.stopPropagation()}
            sx={{
              backgroundColor: theme.palette.messageMenuDropdown.bg,
              "&:hover, &.Mui-focusVisible, &.Mui-selected": {
                backgroundColor: theme.palette.messageMenuDropdown.bg,
              },
              p: 0,
            }}
          >
            <Box
              sx={{ display: "flex", flexDirection: "column", width: "100%" }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  px: { xs: 1, md: 2 },
                  pt: { xs: 1, md: 2 },
                  gap: { xs: 0.75, md: 1 },
                }}
              >
                <TagInput
                  type="text"
                  value={tagText}
                  onChange={(e) => setTagText(e.target.value)}
                  onKeyDown={(e) => {
                    e.stopPropagation();
                    if (e.key === "Enter") onTagSubmit();
                  }}
                  placeholder="Enter tag"
                  onMouseDown={(e) => e.stopPropagation()}
                  onFocus={handleInputFocus}
                  maxLength={20}
                />

                <SendButton
                  whileTap={{ scale: 0.95 }}
                  onClick={onTagSubmit}
                  onMouseDown={(e) => e.stopPropagation()}
                >
                  {tagMode === "add" ? "Send" : "Save"}
                </SendButton>
              </Box>
              {tagMode === "add" && (
                <Box sx={{ px: { xs: 1, md: 2 }, py: { xs: 0.5, md: 1 } }}>
                  {isCompactLayout ? (
                    <Box
                      onMouseDown={(e) => e.stopPropagation()}
                      sx={{
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: 1,
                        overflow: "visible",
                        bgcolor:
                          theme.palette.mode === "light"
                            ? "#fff"
                            : theme.palette.grey[900],
                      }}
                    >
                      <Box
                        role="button"
                        tabIndex={0}
                        onClick={() => {
                          if (suggestions.length === 0) return;
                          setTagSuggestionsOpen((o) => !o);
                        }}
                        onKeyDown={(e) => {
                          if (e.key !== "Enter" && e.key !== " ") return;
                          e.preventDefault();
                          if (suggestions.length === 0) return;
                          setTagSuggestionsOpen((o) => !o);
                        }}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          px: 1.5,
                          py: 1,
                          cursor:
                            suggestions.length === 0 ? "not-allowed" : "pointer",
                          opacity: suggestions.length === 0 ? 0.6 : 1,
                          fontSize: { xs: "12px", sm: "12.5px", md: "13px" },
                          color: theme.palette.text.primary,
                          userSelect: "none",
                        }}
                      >
                        <span>
                          {suggestions.length ? "Suggestions" : "No tags"}
                        </span>
                        <ArrowDropDownIcon
                          sx={{
                            fontSize: 20,
                            transition: "transform 0.2s ease",
                            transform: tagSuggestionsOpen
                              ? "rotate(180deg)"
                              : "rotate(0deg)",
                          }}
                        />
                      </Box>
                      {tagSuggestionsOpen && suggestions.length > 0 && (
                        <Box
                          sx={{
                            borderTop: `1px solid ${theme.palette.divider}`,
                          }}
                        >
                          {suggestions.map((s) => (
                            <Box
                              key={s}
                              onMouseDown={(e) => e.stopPropagation()}
                              onClick={() => {
                                setTagText(s);
                                setTagSuggestionsOpen(false);
                              }}
                              sx={{
                                px: 1.5,
                                py: { xs: 0.375, sm: 0.5 },
                                cursor: "pointer",
                                fontSize: { xs: "11px", sm: "12px", md: "13px" },
                                lineHeight: 1.25,
                                color: theme.palette.text.primary,
                                overflow: "visible",
                                "&:hover": {
                                  bgcolor:
                                    theme.palette.mode === "light"
                                      ? theme.palette.grey[100]
                                      : theme.palette.grey[800],
                                },
                              }}
                            >
                              {s}
                            </Box>
                          ))}
                        </Box>
                      )}
                    </Box>
                  ) : (
                    <FormControl
                      fullWidth
                      size="small"
                      disabled={suggestions.length === 0}
                    >
                      <Select
                        displayEmpty
                        value={tagText}
                        disabled={suggestions.length === 0}
                        onChange={(e: SelectChangeEvent<string>) =>
                          setTagText(e.target.value as string)
                        }
                        renderValue={() =>
                          suggestions.length ? "Suggestions" : "No tags"
                        }
                        MenuProps={{
                          disablePortal: false,
                          anchorOrigin: {
                            vertical: "top",
                            horizontal: "left",
                          },
                          transformOrigin: {
                            vertical: "bottom",
                            horizontal: "left",
                          },
                          sx: { zIndex: theme.zIndex.tooltip + 1 },
                          PaperProps: {
                            sx: {
                              maxHeight: { xs: 100, md: 150 },
                              overflowY: "auto",
                              bgcolor:
                                theme.palette.mode === "light"
                                  ? "#fff"
                                  : theme.palette.grey[900],
                              "&::-webkit-scrollbar": {
                                width: "8px",
                              },
                              "&::-webkit-scrollbar-track": {
                                backgroundColor:
                                  theme.palette.background.paper,
                                borderRadius: 1,
                              },
                              "&::-webkit-scrollbar-thumb": {
                                backgroundColor: theme.palette.primary.main,
                                borderRadius: 1,
                              },
                              scrollbarWidth: "thin",
                              scrollbarColor: `${theme.palette.primary.main} ${theme.palette.background.paper}`,
                            },
                          },
                        }}
                      >
                        {suggestions.map((s) => (
                          <MenuItem key={s} value={s}>
                            {s}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                </Box>
              )}
              {copyMessage && (
                <Typography
                  variant="caption"
                  color="error"
                  sx={{ px: { xs: 1, md: 2 }, pb: { xs: 0.5, md: 1 }, mt: 0.25 }}
                >
                  {copyMessage}
                </Typography>
              )}
            </Box>
          </MenuItem>
        )}

        {existingTags.length > 0 && (
          <MenuItem
            disableRipple
            sx={{
              ...menuItemStyles(theme),
              cursor: "default",
              fontWeight: 600,
              fontSize: { xs: "12px", md: "13px" },
              alignItems: "flex-start",
              color: theme.palette.text.secondary,
              "&:hover": {
                color: theme.palette.text.secondary,
                backgroundColor: theme.palette.messageMenuDropdown.bg,
              },
            }}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <Box sx={{ py: { xs: "4px", md: "7px" }, pr: { xs: "4px", md: "7px" } }}>
              Current Tags:
            </Box>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(2, 1fr)",
                gap: { xs: 0.5, md: 1 },
              }}
            >
              {existingTags.map((tag, idx) => (
                <Box
                  key={idx}
                  onClick={() => {
                    setTagMode("edit");
                    setTagInputVisible(true);
                    setTagText(tag);
                    setEditingTagIndex(idx);
                  }}
                  onMouseDown={(e) => e.stopPropagation()}
                  sx={{
                    px: { xs: 1, md: 2 },
                    py: { xs: 0.5, md: 1 },
                    fontSize: { xs: "12px", md: "13px" },
                    fontWeight: 500,
                    backgroundColor:
                      editingTagIndex === idx
                        ? theme.palette.mode === "light"
                          ? "#E3F2FD"
                          : "#1E293B"
                        : theme.palette.messageMenuDropdown.bg,
                    border:
                      editingTagIndex === idx
                        ? `1px solid ${theme.palette.primary.main}`
                        : "none",
                    boxShadow:
                      editingTagIndex === idx
                        ? "0 1px 4px rgba(0, 0, 0, 0.15)"
                        : "none",
                    borderRadius: "4px",
                    transition: "all 0.2s ease-in-out",
                    cursor: "pointer",
                  }}
                >
                  {tag}
                </Box>
              ))}
            </Box>
          </MenuItem>
        )}
      </Menu>

      <MessageFeedbackModal
        open={isFeedbackModalOpen}
        onClose={handleCloseFeedbackModal}
        message={message}
        previousUserMessage={previousUserMessage}
        onFeedbackSubmitted={onFeedbackSubmitted}
        text={feedbackText}
      />
    </>
  );
};

export default MessageMenuDropdown;
